import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { TruckBidT } from '@store/capacityTools/types';
import { LineItemViewModel } from '@typeDefinitions/lineItemTypes';
import { isEmpty } from 'lodash';
import * as yup from 'yup';

import { MAX_RATE_BOOKED_STATUSES } from './constants';
import { ContactFormValues } from './types';

const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{8,}$/;

type MaxRateLoad = {
  maxPrice?: unknown;
  truckBids?: TruckBidT[] | null;
  mode?: string | null;
  status?: string | null;
  lineItems?:
    | Pick<LineItemViewModel, 'quantity' | 'rate' | 'operationType'>[]
    | null;
};

export type CarrierMaxRateBlock = 'booking' | 'booked';

export const getBookedBidAmount = (load: MaxRateLoad | undefined) => {
  const amounts = (load?.truckBids ?? [])
    .filter(
      (bid) =>
        !bid.archived && (bid.status === 'Booked' || bid.status === 'Assigned'),
    )
    .map((bid) => bid.counterAmount ?? bid.amount)
    .filter(
      (amount): amount is number =>
        typeof amount === 'number' && Number.isFinite(amount),
    );

  return amounts.length > 0 ? Math.max(...amounts) : null;
};

export const isMaxRateEnforcementEnabled = () =>
  isFeatureFlagEnabled(FeatureFlags.CarrierPayPreventPayLineItemsOverMaxRate)();

export const toFiniteNumber = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const computeCarrierLineItemsTotal = (
  lineItems: MaxRateLoad['lineItems'],
) =>
  (lineItems ?? [])
    .filter((item) => item.operationType !== 'Delete')
    .reduce(
      (acc, item) =>
        acc +
        (toFiniteNumber(item.quantity) ?? 0) * (toFiniteNumber(item.rate) ?? 0),
      0,
    );

const getBookingStage = (
  status: string | null | undefined,
): CarrierMaxRateBlock | null => {
  if (['Unassigned', 'Can Get'].includes(String(status ?? ''))) {
    return 'booking';
  }
  if (MAX_RATE_BOOKED_STATUSES.includes(String(status ?? ''))) {
    return 'booked';
  }
  return null;
};

export const getCarrierMaxRateBlock = (
  load: MaxRateLoad | undefined,
  enforcementEnabled = isMaxRateEnforcementEnabled(),
): CarrierMaxRateBlock | null => {
  if (!enforcementEnabled) {
    return null;
  }
  if (!['TL', 'Partial'].includes(String(load?.mode ?? ''))) {
    return null;
  }
  const bookingStage = getBookingStage(load?.status);
  if (bookingStage === null) {
    return null;
  }

  const carrierTotal = computeCarrierLineItemsTotal(load?.lineItems);
  if (carrierTotal <= 0) {
    return null;
  }

  const allowedAmounts = [
    getBookedBidAmount(load),
    toFiniteNumber(load?.maxPrice),
  ].filter((amount): amount is number => amount !== null);
  if (
    allowedAmounts.length > 0 &&
    Math.round(carrierTotal * 100) <=
      Math.round(Math.max(...allowedAmounts) * 100)
  ) {
    return null;
  }
  return bookingStage;
};

export const contactSchema = yup.object({
  name: yup.string().required('Name is required'),
  type: yup.string().required('Type are required').notRequired(),
  phone1: yup.string().when((value) => {
    return isEmpty(value[0])
      ? yup.string().nullable().notRequired()
      : yup.string().min(10, 'Phone number must be at least 10 digits');
  }),
  extension1: yup.string().nullable(),
  phone2: yup.string().when((value) => {
    return isEmpty(value[0])
      ? yup.string().nullable().notRequired()
      : yup.string().min(10, 'Phone number must be at least 10 digits');
  }),
  extension2: yup.string().nullable().notRequired(),
  fax: yup.string().when((value) => {
    return isEmpty(value[0])
      ? yup.string().nullable().notRequired()
      : yup.string().min(10, 'Fax number must be at least 10 digits');
  }),
  email: yup.string().nullable().email('Please enter a valid Email'),
  cpn: yup.string().notRequired(),
  emailVerified: yup.boolean().notRequired(),
  phoneVerified: yup.boolean().notRequired(),
  userName: yup
    .string()
    .nullable()
    .test({
      name: 'userName',
      test: function (value, context) {
        const { id } = context.parent as ContactFormValues;
        if (Boolean(id)) {
          return true;
        }
        if (!isEmpty(value) && (value || '').trim().length < 7) {
          return this.createError({
            message:
              'Username for a contact must be at least 7 characters and cannot be all whitespaces',
          });
        }
        return true;
      },
    }),
  password: yup
    .string()
    .nullable()
    .test({
      name: 'password',
      test: function (value, context) {
        const { userName, type, id } = context.parent as ContactFormValues;
        if (Boolean(id)) {
          return true;
        }
        if (
          (type === 'Billing' || type === 'Freight') &&
          Boolean(userName) &&
          !Boolean(value)
        ) {
          return this.createError({
            message: 'Password is required',
          });
        }
        if (Boolean(value) && !passwordRegex.test(value!)) {
          return this.createError({
            message:
              'Password for a contact must have one upper case letter, one lower case letter, and a digit, and be at least 8 characters in length',
          });
        }
        return true;
      },
    }),
});

const customerContactPhoneSchema = yup.string().when((value) => {
  return isEmpty(value[0])
    ? yup.string().nullable().notRequired()
    : yup
        .string()
        .min(10, 'Phone number must be at least 10 digits')
        .max(20, 'Phone number must be at most 20 digits');
});

export const customerContactSchema = contactSchema.shape({
  phone1: customerContactPhoneSchema,
  phone2: customerContactPhoneSchema,
});

export const lineItemSchemaFactory = (section: string) => {
  return yup.object().shape({
    type: yup.string().required('Type is required'),
    description: yup
      .string()
      .nullable()
      .max(150, 'Description must be no more than 150 characters')
      .meta({ section }),
    quantity: yup
      .mixed()
      .transform((value: unknown) => {
        if (value === '') return undefined;
        return value;
      })
      .required('Quantity is required')
      .test('is-number', 'Quantity must be a number', (value: unknown) => {
        if (value === null || value === undefined) return true;
        return !isNaN(Number(value));
      })
      .test(
        'max-value',
        'Quantity must be less than 99999',
        (value: unknown) => {
          if (value === null || value === undefined) return true;
          return Number(value) <= 99999;
        },
      )
      .meta({ section }),

    rate: yup
      .mixed()
      .transform((value: unknown) => {
        if (value === '') return undefined;
        return value;
      })
      .required('Rate is required')
      .test('is-number', 'Rate must be a number', (value: unknown) => {
        if (value === null || value === undefined) return true;
        return !isNaN(Number(value));
      })
      .test(
        'max-value',
        'Rate must be less than 10000000',
        (value: unknown) => {
          if (value === null || value === undefined) return true;
          return Number(value) <= 10000000;
        },
      )
      .meta({ section }),

    estimate: yup.boolean().nullable(),
  });
};
